import { articles, type Article, type ArticleCategory } from "@/mocks/articles";

export type SortKey = "latest" | "hot";

export function getAllArticles(): Article[] {
  return articles;
}

export function getArticleById(id: string): Article | undefined {
  return articles.find((item) => item.id === id);
}

export function getArticlesByCategory(category: ArticleCategory): Article[] {
  return articles.filter((item) => item.category === category);
}

export function getFeaturedArticles(limit = 4): Article[] {
  // Prefer landscape covers (news / stars) for the hero carousel to avoid cropping posters.
  const featuredLandscape = articles.filter(
    (item) => item.featured && item.category !== "recommendations",
  );
  const latestLandscape = sortArticles(
    articles.filter((item) => item.category !== "recommendations"),
    "latest",
  );
  const merged: Article[] = [];
  [...featuredLandscape, ...latestLandscape].forEach((item) => {
    if (merged.length < limit && !merged.some((exist) => exist.id === item.id)) {
      merged.push(item);
    }
  });
  return merged;
}

export function getHotArticles(limit = 4): Article[] {
  return [...articles].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getRelatedArticles(article: Article, limit = 3): Article[] {
  const sameCategory = articles.filter(
    (item) => item.category === article.category && item.id !== article.id,
  );
  const others = articles.filter(
    (item) => item.category !== article.category && item.id !== article.id,
  );
  return [...sameCategory, ...others].slice(0, limit);
}

export function sortArticles(list: Article[], sortKey: SortKey): Article[] {
  if (sortKey === "hot") {
    return [...list].sort((a, b) => b.views - a.views);
  }
  return [...list].sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  );
}

export function paginate<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function totalPages(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export function searchArticles(query: string, category?: ArticleCategory | "all"): Article[] {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return [];
  return articles.filter((item) => {
    if (category && category !== "all" && item.category !== category) return false;
    const haystack = [item.title, item.summary, item.tags.join(" "), item.content.join(" ")]
      .join(" ")
      .toLowerCase();
    return haystack.includes(keyword);
  });
}

export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return String(views);
}